import { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Icon, Label, Link, Text } from '@nimbus-ds/components';
import { EditIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import Domain from '@tiendanube/common/src/enums/Domain';
import { InterfaceNameValue, RadioButtonGroup } from '@tiendanube/components';
import { useBoolean } from 'commons/hooks';
import {
  getProductNameWithVariant,
  ProductInterface,
  ProductSearcher,
  ProductSearcherFooterProps,
  ProductSearcherConfirmOrderFilters,
} from '../ProductSearcher';

export interface ProductsFilterValue {
  products: ProductInterface<true>[];
  exactProductSet: string;
}

interface ProductsFilterProperties {
  selectedProducts: ProductInterface<true>[];
  exactProductSet: boolean;
  onProductsFilterChange: (value: ProductsFilterValue) => void;
}

const ALL_PRODUCTS = 'all';
const CUSTOM_PRODUCTS = 'custom';
const SELECTED_PRODUCTS_LIMIT = 25;

function ProductsFilter({
  selectedProducts,
  exactProductSet = false,
  onProductsFilterChange,
}: ProductsFilterProperties): JSX.Element {
  const { t } = useTranslation(Domain.ORDERS);

  const [isProductSearcherOpen, openProductSearcher, closeProductSearcher] =
    useBoolean(false);
  const [exactProductSetEnabled, setExactProductSetEnabled] =
    useState<boolean>(exactProductSet);

  const resetSelectedOption = useCallback(() => {
    if (!selectedProducts.length) {
      setProductsFilterOptionSelected(ALL_PRODUCTS);
      setExactProductSetEnabled(false);
    }
  }, [selectedProducts]);

  const handleProductSearcherClose = useCallback(() => {
    closeProductSearcher();
    resetSelectedOption();
  }, [closeProductSearcher, resetSelectedOption]);

  const handleProductsChange = useCallback(
    (products: ProductInterface<true>[]) => {
      onProductsFilterChange({
        products: products,
        exactProductSet:
          products.length > 0 ? String(exactProductSetEnabled) : '',
      });
      closeProductSearcher();
      if (!products.length) {
        resetSelectedOption();
      }
    },
    [
      onProductsFilterChange,
      exactProductSetEnabled,
      closeProductSearcher,
      resetSelectedOption,
    ],
  );

  const [productsFilterOptionSelected, setProductsFilterOptionSelected] =
    useState<string>(selectedProducts.length ? CUSTOM_PRODUCTS : ALL_PRODUCTS);

  const options = [
    {
      label: t('filter.productsFilter.allProducts'),
      value: ALL_PRODUCTS,
    },
    {
      label: t('filter.productsFilter.customProducts'),
      value: CUSTOM_PRODUCTS,
    },
  ];

  const handleChangeOption = (e: InterfaceNameValue) => {
    if (e.value === CUSTOM_PRODUCTS) {
      openProductSearcher();
    } else if (e.value === ALL_PRODUCTS) {
      onProductsFilterChange({ products: [], exactProductSet: '' });
      setExactProductSetEnabled(false);
    }
    setProductsFilterOptionSelected(e.value);
  };

  const handleRemoveProduct = useCallback(
    (variantId: string) => {
      handleProductsChange(
        selectedProducts.filter((product) => product.variantId !== variantId),
      );
      if (selectedProducts.length === 0) {
        setExactProductSetEnabled(false);
      }
    },
    [selectedProducts, handleProductsChange],
  );

  const handleExactProductSetChange = useCallback(() => {
    setExactProductSetEnabled(!exactProductSetEnabled);
    onProductsFilterChange({
      products: selectedProducts,
      exactProductSet: String(!exactProductSetEnabled),
    });
  }, [exactProductSetEnabled, onProductsFilterChange, selectedProducts]);

  useEffect(() => {
    resetSelectedOption();
  }, [resetSelectedOption]);

  const onRenderFooter = useCallback(
    (footerProps: ProductSearcherFooterProps<true>) => (
      <ProductSearcherConfirmOrderFilters
        footerProps={footerProps}
        exactProductSet={exactProductSetEnabled}
        onExactProductSetChange={handleExactProductSetChange}
      />
    ),
    [exactProductSetEnabled, handleExactProductSetChange],
  );

  return (
    <Box borderStyle="none">
      <Box paddingBottom="2" display="flex" alignItems="flex-start" gap="1">
        <Label>{t('filter.productsFilter.title')}</Label>
      </Box>
      <Box>
        <RadioButtonGroup
          name="productsOptionFilter"
          options={options}
          onChange={handleChangeOption}
          value={productsFilterOptionSelected}
        />
      </Box>
      {!!selectedProducts.length && (
        <Box>
          <Box
            paddingTop="1"
            paddingBottom="2"
            display="flex"
            flex="1"
            flexDirection="row"
            flexWrap="wrap"
          >
            {selectedProducts.map((product) => (
              <Box
                key={product.variantId}
                paddingY="1"
                paddingLeft="none"
                paddingRight="2"
              >
                <Chip
                  text={getProductNameWithVariant(product)}
                  removable
                  onClick={() => handleRemoveProduct(product.variantId)}
                />
              </Box>
            ))}
          </Box>
          <Link textDecoration="none" onClick={openProductSearcher}>
            <Icon color="primary-interactive" source={<EditIcon />} />
            <Text color="primary-interactive" fontSize="base">
              {t('filter.productsFilter.editProducts')}
            </Text>
          </Link>
        </Box>
      )}
      <ProductSearcher<true>
        onClose={handleProductSearcherClose}
        onSubmit={handleProductsChange}
        isOpen={isProductSearcherOpen}
        preselectedProducts={selectedProducts}
        selectedProductsLimit={SELECTED_PRODUCTS_LIMIT}
        minimalistic
        renderFooterCallback={onRenderFooter}
        showPrice
        allowInfiniteStockSelection
      />
    </Box>
  );
}

export default ProductsFilter;
